/**
 * TakeMeasureGoalsPanel — the per-calibration-take "Goal measurements"
 * table (Goal Score PR-7b, `docs/goal_score/F_ui_ux_designs.md` §5,
 * mockup `04_supporting_video_uploads.html`).
 *
 * Sits BELOW a take's existing grade/measured-score badges in
 * `CalibrationTakeList.tsx` — a take's grade (used for Check calibration)
 * and its Goal measurements (used to sanity-check Goal targets against
 * real examples) are different purposes on the same object, per F §5.
 *
 * Mirrors `TravelDistanceEditor`'s own explicit-action contract for the
 * existing travel preview: "Measure goals now" is a button, never fired
 * automatically on mount or on a keystroke — the backend's own
 * recompute-fresh rule (never cached) means every click is a real,
 * non-trivial measurement pass.
 */

import { useState } from 'react';
import { measureCalibrationTakeGoals } from '@/services/goalAuthoringApi';
import {
  deriveGoalMeasurementDisplayStatus,
  type TakeGoalMeasurement,
  type GoalKind,
} from '@/types/goalAuthoring';
import {
  TAKE_GOALS_PANEL_TITLE,
  TAKE_GOALS_MEASURE_BUTTON,
  TAKE_GOALS_MEASURING,
  TAKE_GOALS_EMPTY_NOTE,
  TAKE_GOALS_ERROR,
  TAKE_GOAL_STATUS_LABELS,
  TAKE_GOAL_ABSTAIN_HOW,
  GOAL_KIND_LABELS,
} from '@/utils/goalAuthoringCopy';
import { cn } from '@/utils/cn';

type PanelState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ok'; goals: TakeGoalMeasurement[] }
  | { status: 'error' };

export interface TakeMeasureGoalsPanelProps {
  templateId: string;
  takeId: string;
  className?: string;
}

/** Plain units per kind — no landmark/axis vocabulary, per the copy
 * standard's denylist (which also bans the word "landmark" itself). */
function unitSuffix(kind: GoalKind): string {
  if (kind === 'angle') return '°';
  if (kind === 'speed') return ' body heights/s';
  return ' body heights';
}

function formatNumber(value: number | null): string {
  if (value === null) return '—';
  return value.toFixed(2);
}

const STATUS_BADGE_CLASSES: Record<string, string> = {
  exceeded: 'bg-success/15 text-success border-success/30',
  met: 'bg-info/15 text-info border-info/30',
  missed: 'bg-dark-elevated-2 text-text-secondary border-border-subtle',
  abstained: 'bg-warning/15 text-warning border-warning/30',
};

export default function TakeMeasureGoalsPanel({
  templateId,
  takeId,
  className,
}: TakeMeasureGoalsPanelProps) {
  const [state, setState] = useState<PanelState>({ status: 'idle' });

  const handleMeasure = () => {
    setState({ status: 'loading' });
    measureCalibrationTakeGoals(templateId, takeId)
      .then((response) => {
        setState({ status: 'ok', goals: response.goals });
      })
      .catch(() => {
        setState({ status: 'error' });
      });
  };

  return (
    <div
      className={cn('mt-2 rounded-lg border border-border-subtle bg-dark-elevated-1 p-3 space-y-2', className)}
      data-testid="take-measure-goals-panel"
    >
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-text-primary">{TAKE_GOALS_PANEL_TITLE}</p>
        <button
          type="button"
          onClick={handleMeasure}
          disabled={state.status === 'loading'}
          className="text-xs font-medium px-2.5 py-1 rounded-md border border-accent-indigo/40 text-accent-indigo hover:bg-accent-indigo/10 transition-colors disabled:opacity-50"
          data-testid="take-measure-goals-button"
        >
          {state.status === 'loading' ? TAKE_GOALS_MEASURING : TAKE_GOALS_MEASURE_BUTTON}
        </button>
      </div>

      {state.status === 'error' && (
        <p className="text-xs text-danger" data-testid="take-measure-goals-error">
          {TAKE_GOALS_ERROR}
        </p>
      )}

      {state.status === 'ok' && state.goals.length === 0 && (
        <p className="text-xs text-text-muted" data-testid="take-measure-goals-empty">
          {TAKE_GOALS_EMPTY_NOTE}
        </p>
      )}

      {state.status === 'ok' && state.goals.length > 0 && (
        <div className="space-y-1.5" data-testid="take-measure-goals-table">
          {state.goals.map((g) => {
            const displayStatus = deriveGoalMeasurementDisplayStatus(g);
            return (
              <div
                key={g.goal_key}
                className="flex items-center justify-between gap-2 py-1 px-2 rounded bg-dark-card"
                data-testid={`take-goal-row-${g.goal_key}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-text-primary truncate">
                    {g.label ?? GOAL_KIND_LABELS[g.kind]}
                  </p>
                  {displayStatus === 'abstained' ? (
                    <p className="text-[11px] text-text-muted">
                      {g.abstention_reason ?? TAKE_GOAL_ABSTAIN_HOW}
                    </p>
                  ) : (
                    <p className="text-[11px] text-text-muted">
                      {formatNumber(g.measured)}
                      {unitSuffix(g.kind)}
                      {' — target '}
                      {formatNumber(g.target)}
                      {unitSuffix(g.kind)}
                    </p>
                  )}
                </div>
                <span
                  className={cn(
                    'flex-shrink-0 text-[11px] font-medium px-2 py-0.5 rounded-full border',
                    STATUS_BADGE_CLASSES[displayStatus],
                  )}
                  data-testid={`take-goal-status-${g.goal_key}`}
                >
                  {TAKE_GOAL_STATUS_LABELS[displayStatus]}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
